import type { Edge, Node } from '@xyflow/react';
import i18next from '../../i18n/initI18n';

/**
 * Визуальные цепочки сохраняют JSON узлов (граф + поля шага).
 * Списки получателей и каталоги отправителей подгружаются из сервиса отправки:
 * в этом хранилище достаточно **выбранных id/ключей** и текста/настроек шага,
 * без копирования полных справочников.
 */

/** Каналы рассылки */
export type MailingChannel = 'push' | 'sms' | 'email' | 'viber' | 'waba';

export const CHANNEL_META: Record<
  MailingChannel,
  { label: string; color: string; short: string }
> = {
  push: { label: 'Push', color: '#11C2ED', short: 'Push' },
  sms: { label: 'SMS', color: '#FFB300', short: 'SMS' },
  email: { label: 'Email', color: '#0084A4', short: 'Email' },
  viber: { label: 'Viber', color: '#7360F2', short: 'Viber' },
  waba: { label: 'WABA', color: '#32D951', short: 'WABA' },
};

/** Каналы в палитре редактора (перетаскивание). Отключённые видны в списке, на холст не ставятся. */
export const MAIL_EDITOR_PALETTE_CHANNELS: readonly MailingChannel[] = [
  'push',
  'sms',
  'email',
  'viber',
  'waba',
];

/** Каналы «в разработке»: в палитре отображаются, перетащить на холст нельзя. */
export const MAIL_EDITOR_PALETTE_DISABLED_CHANNELS: ReadonlySet<MailingChannel> = new Set([
  'push',
  'waba',
]);

/** Верхняя граница числа блоков «Ожидание» на одной схеме (защита от случайного засорения). */
export const MAIL_EDITOR_MAX_DELAY_BLOCKS = 10;

/** В event-цепочке — несколько блоков одного канала (шаги −10/−5/…). */
export const MAIL_EDITOR_MAX_EVENT_CHANNEL_BLOCKS = 10;

export type LaunchMode = 'schedule' | 'event';
export type EventTriggerType = 'birthday' | 'registration' | 'custom';
export type StartNodeData = {
  trigger: 'manual' | 'schedule' | 'event';
  /** schedule = разовая дата; event = триггер по полю списка. */
  launchMode?: LaunchMode;
  scheduleCron?: string;
  scheduleDate?: string;
  scheduleTime?: string;
  eventType?: EventTriggerType;
  eventFieldKey?: string;
  /** IANA timezone from browser (e.g. Europe/Minsk) — время проверки в этом поясе. */
  eventTimezone?: string;
};

/** Часовой пояс браузера пользователя (IANA). */
export function browserIanaTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz?.trim() || 'Europe/Minsk';
  } catch {
    return 'Europe/Minsk';
  }
}

export function visualChainIsEventTrigger(nodes: readonly { type?: string; data?: unknown }[]): boolean {
  const start = nodes.find((n) => n.type === 'mailingStart');
  if (!start) return false;
  const d = normalizeStartNodeData(start.data);
  return d.launchMode === 'event' || d.trigger === 'event';
}

export function normalizeStartNodeData(raw: unknown): StartNodeData {
  const r = raw as Partial<StartNodeData>;
  const trig = r.trigger;
  const launchModeRaw = r.launchMode;
  const launchMode: LaunchMode =
    launchModeRaw === 'event' || trig === 'event'
      ? 'event'
      : launchModeRaw === 'schedule' || trig === 'schedule'
        ? 'schedule'
        : 'schedule';
  const trigger: StartNodeData['trigger'] =
    launchMode === 'event' ? 'event' : launchMode === 'schedule' ? 'schedule' : 'manual';
  const scheduleCron =
    r.scheduleCron != null && String(r.scheduleCron).trim() !== ''
      ? String(r.scheduleCron)
      : undefined;
  const eventTypeRaw = r.eventType;
  const eventType: EventTriggerType =
    eventTypeRaw === 'registration' || eventTypeRaw === 'custom' ? eventTypeRaw : 'birthday';
  return {
    trigger,
    launchMode,
    ...(scheduleCron !== undefined ? { scheduleCron } : {}),
    scheduleDate: String(r.scheduleDate ?? ''),
    scheduleTime: String(r.scheduleTime ?? ''),
    eventType,
    eventFieldKey: String(r.eventFieldKey ?? ''),
    eventTimezone: String(r.eventTimezone ?? ''),
  };
}

export function startScheduleDateTimeMs(scheduleDate?: string | null, scheduleTime?: string | null): number | null {
  const date = String(scheduleDate ?? '').trim();
  const time = String(scheduleTime ?? '').trim();
  if (!date || !time) return null;
  const ms = new Date(`${date}T${time}`).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function startScheduleIsPast(scheduleDate?: string | null, scheduleTime?: string | null, nowMs = Date.now()): boolean {
  const ms = startScheduleDateTimeMs(scheduleDate, scheduleTime);
  return ms != null && ms < nowMs;
}

export function todayInputDateValue(now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function currentInputTimeValue(now = new Date()): string {
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** Тип контента рассылки Viber на узле. */
export type ViberMessageType = 'plain' | 'withButton' | 'image';

/** Данные узла канала: у каждого мессенджера свой набор полей. */
export type ChannelNodeData =
  | {
      channel: 'push';
      title: string;
      body: string;
      imageUrl: string;
      actionUrl: string;
      scheduleDate: string;
      scheduleTime: string;
      /** Id/ключ отправителя из каталога сервиса (не копия справочника). */
      senderName: string;
    }
  | {
      channel: 'sms';
      body: string;
      /** Id ключа отправителя у провайдера / альфа-имя по договорённости с API. */
      senderName: string;
      /** Откуда брать получателей при отправке. */
      recipientMode: 'list' | 'manual';
      /** Id или ключ списка из сервиса отправки (не содержимое списка). */
      contactListQuery: string;
      /** Id шаблона у провайдера (sms/create templatesMessages), если есть в каталоге. */
      messageTemplateQuery: string;
      /** Режим manual: номера для теста или источник вне этой базы. */
      phonesManual: string;
      scheduleDate: string;
      scheduleTime: string;
      /** Дней до события (0 = в день); только для launchMode=event. */
      triggerOffsetDays: string;
    }
  | {
      channel: 'email';
      title: string;
      body: string;
      preheader: string;
      scheduleDate: string;
      scheduleTime: string;
      /** Id подтверждённого отправителя (GET email/sendersconfirm, addresses.id). */
      senderName: string;
      /** Имя отправителя (поле sender_name у провайдера / «От кого» в почте). */
      senderDisplayName: string;
      /** Ref HTML-шаблона (POST email/templates samples[].id, GET email/template/{ref}). */
      messageTemplateQuery: string;
      /** Устарело: UI не использует; получатели — списки контактов кампании (как SMS/Viber). */
      emailContactListId: string;
      triggerOffsetDays: string;
    }
  | {
      channel: 'viber';
      /** Id отправителя Viber из каталога сервиса отправки. */
      viberId: string;
      messageType: ViberMessageType;
      /** Id типа сообщения из API провайдера (напр. TEXT, BUTTON); пусто — только внутренний messageType. */
      viberMessageKindId: string;
      recipientMode: 'list' | 'manual';
      /** Id или ключ списка из сервиса отправки (не состав списка). */
      contactListQuery: string;
      phonesManual: string;
      /** Id или ключ шаблона у провайдера. */
      messageTemplateQuery: string;
      scheduleDate: string;
      scheduleTime: string;
      body: string;
      buttonText: string;
      buttonUrl: string;
      /** Для типа «Изображение». */
      imageUrl: string;
      triggerOffsetDays: string;
    }
  | {
      channel: 'waba';
      body: string;
      templateName: string;
      languageCode: string;
      scheduleDate: string;
      scheduleTime: string;
      /** Id/ключ бизнес-отправителя из каталога сервиса. */
      senderName: string;
    };

export function defaultChannelNodeData(channel: MailingChannel): ChannelNodeData {
  switch (channel) {
    case 'push':
      return {
        channel: 'push',
        title: '',
        body: '',
        imageUrl: '',
        actionUrl: '',
        scheduleDate: '',
        scheduleTime: '',
        senderName: '',
      };
    case 'sms':
      return {
        channel: 'sms',
        body: '',
        senderName: '',
        recipientMode: 'list',
        contactListQuery: '',
        messageTemplateQuery: '',
        phonesManual: '',
        scheduleDate: '',
        scheduleTime: '',
        triggerOffsetDays: '',
      };
    case 'email':
      return {
        channel: 'email',
        title: '',
        body: '',
        preheader: '',
        scheduleDate: '',
        scheduleTime: '',
        senderName: '',
        senderDisplayName: '',
        messageTemplateQuery: '',
        emailContactListId: '',
        triggerOffsetDays: '',
      };
    case 'viber':
      return {
        channel: 'viber',
        viberId: '',
        messageType: 'plain',
        viberMessageKindId: '',
        recipientMode: 'list',
        contactListQuery: '',
        phonesManual: '',
        messageTemplateQuery: '',
        scheduleDate: '',
        scheduleTime: '',
        body: '',
        buttonText: '',
        buttonUrl: '',
        imageUrl: '',
        triggerOffsetDays: '',
      };
    case 'waba':
      return {
        channel: 'waba',
        body: '',
        templateName: '',
        languageCode: 'ru',
        scheduleDate: '',
        scheduleTime: '',
        senderName: '',
      };
  }
}

/** Поддержка старых сохранений с полями title/body у всех каналов. */
export function normalizeChannelNodeData(raw: unknown): ChannelNodeData {
  const r = raw as Partial<Record<string, unknown>> & { channel?: MailingChannel };
  const ch = r.channel;
  const title = String(r.title ?? '');
  const body = String(r.body ?? '');
  if (ch === 'push') {
    return {
      channel: 'push',
      title,
      body,
      imageUrl: String(r.imageUrl ?? ''),
      actionUrl: String(r.actionUrl ?? r.deepLink ?? ''),
      scheduleDate: String(r.scheduleDate ?? ''),
      scheduleTime: String(r.scheduleTime ?? ''),
      senderName: String(r.senderName ?? ''),
    };
  }
  if (ch === 'sms') {
    const recipientMode = r.recipientMode === 'manual' ? 'manual' : 'list';
    return {
      channel: 'sms',
      body,
      senderName: String(r.senderName ?? ''),
      recipientMode,
      contactListQuery: String(r.contactListQuery ?? ''),
      messageTemplateQuery: String(r.messageTemplateQuery ?? ''),
      phonesManual: String(r.phonesManual ?? ''),
      scheduleDate: String(r.scheduleDate ?? ''),
      scheduleTime: String(r.scheduleTime ?? ''),
      triggerOffsetDays: String(r.triggerOffsetDays ?? ''),
    };
  }
  if (ch === 'email') {
    return {
      channel: 'email',
      title,
      body,
      preheader: String(r.preheader ?? ''),
      scheduleDate: String(r.scheduleDate ?? ''),
      scheduleTime: String(r.scheduleTime ?? ''),
      senderName: String(r.senderName ?? ''),
      senderDisplayName: String(r.senderDisplayName ?? ''),
      messageTemplateQuery: String(r.messageTemplateQuery ?? ''),
      emailContactListId: String(r.emailContactListId ?? ''),
      triggerOffsetDays: String(r.triggerOffsetDays ?? ''),
    };
  }
  if (ch === 'viber') {
    const recipientMode = r.recipientMode === 'manual' ? 'manual' : 'list';
    const mt = r.messageType;
    const messageType: ViberMessageType =
      mt === 'withButton' || mt === 'image' || mt === 'plain' ? mt : 'plain';
    return {
      channel: 'viber',
      viberId: String(r.viberId ?? ''),
      messageType,
      viberMessageKindId: String(r.viberMessageKindId ?? ''),
      recipientMode,
      contactListQuery: String(r.contactListQuery ?? ''),
      phonesManual: String(r.phonesManual ?? ''),
      messageTemplateQuery: String(r.messageTemplateQuery ?? ''),
      scheduleDate: String(r.scheduleDate ?? ''),
      scheduleTime: String(r.scheduleTime ?? ''),
      body,
      buttonText: String(r.buttonText ?? ''),
      buttonUrl: String(r.buttonUrl ?? ''),
      imageUrl: String(r.imageUrl ?? ''),
      triggerOffsetDays: String(r.triggerOffsetDays ?? ''),
    };
  }
  if (ch === 'waba') {
    return {
      channel: 'waba',
      body,
      templateName: String(r.templateName ?? ''),
      languageCode: String(r.languageCode ?? 'ru'),
      scheduleDate: String(r.scheduleDate ?? ''),
      scheduleTime: String(r.scheduleTime ?? ''),
      senderName: String(r.senderName ?? ''),
    };
  }
  return defaultChannelNodeData('sms');
}

/** Единицы ожидания в данных (совместимость со старыми цепочками); в редакторе — только минуты. */
export type DelayTimeUnit = 'seconds' | 'minutes' | 'hours' | 'days';

export const DELAY_MIN_MINUTES = 5;
export const DELAY_MAX_MINUTES = 60;

export type DelayNodeData = {
  value: number;
  unit: DelayTimeUnit;
  /** Ранние сохранения: только минуты */
  minutes?: number;
};

const LEGACY_DELAY_UNITS: readonly DelayTimeUnit[] = ['seconds', 'minutes', 'hours', 'days'];

/** @deprecated только для миграции старых JSON; UI использует только минуты. */
export const DELAY_UNITS_ORDER: readonly DelayTimeUnit[] = ['minutes'];

function legacyUnitToApproxMinutes(val: number, unit: DelayTimeUnit): number {
  const v = Math.max(0, Math.floor(val));
  switch (unit) {
    case 'seconds':
      return Math.max(1, Math.ceil(v / 60));
    case 'minutes':
      return v;
    case 'hours':
      return v * 60;
    case 'days':
      return v * 24 * 60;
    default:
      return v;
  }
}

export function clampDelayMinutes(minutes: number): number {
  return Math.min(DELAY_MAX_MINUTES, Math.max(DELAY_MIN_MINUTES, Math.floor(minutes)));
}

export function normalizeDelayNodeData(raw: unknown): { value: number; unit: DelayTimeUnit } {
  const d = raw as Partial<DelayNodeData> & { minutes?: number };

  let rawVal: number;
  let rawUnit: DelayTimeUnit;

  if (typeof d.value === 'number' && d.unit && LEGACY_DELAY_UNITS.includes(d.unit)) {
    rawVal = d.value;
    rawUnit = d.unit;
  } else if (typeof d.minutes === 'number') {
    rawVal = d.minutes;
    rawUnit = 'minutes';
  } else {
    return { value: DELAY_MIN_MINUTES, unit: 'minutes' };
  }

  const totalMin = legacyUnitToApproxMinutes(Math.max(0, Math.floor(rawVal)), rawUnit);
  return { value: clampDelayMinutes(totalMin), unit: 'minutes' };
}

export type EndNodeData = Record<string, never>;

/** Режим подсказки для узла «Условие» (реализация проверки на стороне сервера). */
export type ConditionMode = 'delivery' | 'click' | 'segment' | 'custom';

export type ConditionNodeData = {
  title: string;
  /** Что проверяем — для команды и реализации на сервере */
  description: string;
  mode: ConditionMode;
};

export function normalizeConditionNodeData(raw: unknown): ConditionNodeData {
  const r = raw as Partial<ConditionNodeData>;
  const modes: ConditionMode[] = ['delivery', 'click', 'segment', 'custom'];
  const mode = modes.includes(r.mode as ConditionMode) ? (r.mode as ConditionMode) : 'custom';
  const defaultTitle = i18next.t('common.condition');
  return {
    title: String(r.title ?? defaultTitle).trim() || defaultTitle,
    description: String(r.description ?? ''),
    mode,
  };
}

export function defaultConditionNodeData(): ConditionNodeData {
  return { title: i18next.t('common.condition'), description: '', mode: 'custom' };
}

/** A/B: две ветки, доля трафика на A в % (остальное — B). */
export type AbTestNodeData = {
  labelA: string;
  labelB: string;
  splitPercentA: number;
};

export function normalizeAbTestNodeData(raw: unknown): AbTestNodeData {
  const r = raw as Partial<AbTestNodeData>;
  let p = Math.round(Number(r.splitPercentA));
  if (Number.isNaN(p)) p = 50;
  p = Math.min(100, Math.max(0, p));
  const la = i18next.t('common.variantA');
  const lb = i18next.t('common.variantB');
  return {
    labelA: String(r.labelA ?? la).trim() || la,
    labelB: String(r.labelB ?? lb).trim() || lb,
    splitPercentA: p,
  };
}

export function defaultAbTestNodeData(): AbTestNodeData {
  return { labelA: i18next.t('common.variantA'), labelB: i18next.t('common.variantB'), splitPercentA: 50 };
}

/** Заметка на схеме — не участвует в доставке, без ручек. */
export type NoteNodeData = { text: string };

export function defaultNoteNodeData(): NoteNodeData {
  return { text: '' };
}

export type MailingNodeData =
  | StartNodeData
  | ChannelNodeData
  | DelayNodeData
  | ConditionNodeData
  | AbTestNodeData
  | EndNodeData;

export type MailingCampaign = {
  id: string;
  name: string;
  /** Время создания (если нет — на клиенте подставляется из updatedAt). */
  createdAt?: number;
  updatedAt: number;
  nodes: Node[];
  edges: Edge[];
  /**
   * Только для линейной цепочки из двух каналов SMS и Viber (без Email): одна рассылка у провайдера с перенаправлением.
   * Если false — две отдельные рассылки в порядке узлов. При наличии Email на схеме не используется (всегда отдельные).
   * По умолчанию на бэкенде считается true, если поле отсутствует.
   */
  viberSmsCascadeLinked?: boolean;
};

export function campaignChannelsUsed(nodes: Node[]): MailingChannel[] {
  const set = new Set<MailingChannel>();
  for (const n of nodes) {
    if (n.type === 'mailingChannel') {
      const d = normalizeChannelNodeData(n.data);
      set.add(d.channel);
    }
  }
  return Array.from(set);
}

const LOGIC_NODE_TYPES = new Set([
  'mailingChannel',
  'mailingDelay',
  'mailingCondition',
  'mailingAbTest',
]);

export function countFlowBlocks(nodes: Node[]): number {
  return nodes.filter((n) => LOGIC_NODE_TYPES.has(String(n.type))).length;
}

/** Счётчики каналов и блоков ожидания на схеме (SMS/Viber/Email — не более одного каждого). */
export function countSmsViberDelayBlocks(nodes: Node[]): {
  sms: number;
  viber: number;
  email: number;
  delay: number;
} {
  let sms = 0;
  let viber = 0;
  let email = 0;
  let delay = 0;
  for (const n of nodes) {
    const nt = String(n.type);
    if (nt === 'mailingDelay') {
      delay += 1;
      continue;
    }
    if (nt === 'mailingChannel') {
      const ch = normalizeChannelNodeData(n.data).channel;
      if (ch === 'sms') sms += 1;
      else if (ch === 'viber') viber += 1;
      else if (ch === 'email') email += 1;
    }
  }
  return { sms, viber, email, delay };
}
